import type { CityConfig } from "@/pages/city-template/types";

export const sahuaritaConfig: CityConfig = {
  city: "Sahuarita",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "sahuarita",
  route: "/cities/sahuarita",
  title: "Emergency & Expedited Passport Services Sahuarita AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Sahuarita, AZ — as fast as 24 hours. No Sahuarita passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 25 miles north. A+ BBB rated. Serving Green Valley, Freeport-McMoRan engineers, and Santa Cruz Valley families.",
  h1: "Emergency & Expedited Passport Services in Sahuarita, AZ",
  heroSubtitle: "Sahuarita sits in the Santa Cruz Valley only 25 miles south of the Western Passport Center in Tucson — one of the closest towns to an Arizona passport agency. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Freeport-McMoRan mining engineer heading to South America, a Green Valley retiree planning international travel, or a Sahuarita family with a Mexico vacation on the calendar.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sahuarita%20Arizona%20scenic%20view%20of%20the%20Santa%20Rita%20Mountains%20at%20golden%20hour%20with%20the%20master-planned%20Rancho%20Sahuarita%20community%20lake%20and%20fountain%20in%20the%20foreground%2C%20modern%20desert%20landscaping%20with%20palm%20trees%20and%20Arizona%20native%20plants%2C%20warm%20sunset%20light%20casting%20long%20shadows%2C%20professional%20wide%20angle%20landscape%20photography%2C%20clean%20composition%2C%20no%20people%20visible&width=1800&height=700&seq=sahuarita-hero-001&orientation=landscape",
  heroImageAlt: "Sahuarita Arizona with Santa Rita Mountains at golden hour sunset",
  geoPosition: "31.9576;-110.9556",
  geoPlacename: "Sahuarita, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 25 miles from Sahuarita — about 30 minutes via I-19",
  },
  nearbyCitiesZips: [
    { name: "Sahuarita", zips: "85629" },
    { name: "Green Valley", zips: "85614, 85622" },
    { name: "Tubac", zips: "85646" },
    { name: "Amado", zips: "85645" },
    { name: "Rio Rico", zips: "85648" },
    { name: "Nogales", zips: "85621" },
    { name: "Patagonia", zips: "85624" },
    { name: "Sonoita", zips: "85637" },
    { name: "Arivaca", zips: "85601" },
    { name: "Vail", zips: "85641" },
    { name: "Corona de Tucson", zips: "85641" },
    { name: "Three Points", zips: "85736" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — only 25 miles from Sahuarita" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Southern AZ Hub", desc: "Serving Sahuarita, Green Valley, Tubac, and all Santa Cruz Valley" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Sahuarita case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Tucson AZ", route: "/cities/tucson" },
    { name: "Expedited passport in Green Valley AZ", route: "/cities/green-valley" },
    { name: "Urgent passport in Nogales AZ", route: "/cities/nogales" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Fast passport in Sierra Vista AZ", route: "/cities/sierra-vista" },
    { name: "Expedited passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Emergency passport in Scottsdale AZ", route: "/cities/scottsdale" },
  ],
  fpcOfficeAddress: "1 S Church Ave, Tucson, AZ 85701",
  fpcOfficeNote: "Fast Passport Center serves Sahuarita remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson — only 25 miles away. Sahuarita's location on the I-19 corridor makes it one of the closest communities to a passport agency in Arizona, enabling exceptionally fast courier turnaround.",
  fpcOfficeDistance: "Serving all of the Santa Cruz Valley",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Sahuarita, Green Valley, Tubac, and the Santa Cruz Valley corridor. From Freeport-McMoRan mining engineers flying to South American operations to Green Valley retirees heading abroad, we help Southern Arizona travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Sahuarita and nearby communities including Green Valley, Tubac, Amado, Rio Rico, Nogales, Patagonia, and the entire Santa Cruz Valley area.",
  howItWorksIntro: "Most Sahuarita travelers complete everything online and ship documents overnight. At only 25 miles from Tucson, Fast Passport Center provides exceptionally fast courier turnaround for Southern Arizona residents — among the quickest in the state given the short distance to the Western Passport Center.",
  mistakesIntro: "Sahuarita travelers, including mining industry engineers flying to South America and Green Valley snowbirds traveling internationally, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Sahuarita and Southern Arizona travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sahuarita? Here are the most common ones we hear from Santa Cruz Valley travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Only 25 miles to Tucson (30-minute drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Freeport-McMoRan engineers heading to South America",
        "Perfect for Green Valley snowbirds traveling internationally",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from TUS or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Sahuarita is only 25 miles from the Western Passport Center in Tucson — among the fastest courier routes in Arizona.",
      bullets: ["Southern AZ coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Sahuarita adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Sahuarita and Santa Cruz Valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Sahuarita and Pima County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Sahuarita, Green Valley, and Santa Cruz Valley residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Pima County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Sahuarita and Southern Arizona residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Sahuarita AZ", description: "Urgent passport processing for Sahuarita, AZ travelers via Western Passport Center courier — only 25 miles from Sahuarita." },
    { name: "Passport Renewal Sahuarita AZ", description: "Fast passport renewal for Sahuarita and Pima County, Arizona residents." },
    { name: "New Passport Sahuarita AZ", description: "First-time passport application assistance for Sahuarita and Santa Cruz Valley area residents." },
    { name: "Lost Passport Sahuarita AZ", description: "Passport replacement for lost or stolen passports for Sahuarita and Southern Arizona residents." },
    { name: "Child Passport Sahuarita AZ", description: "Minor passport services for Sahuarita and Pima County families." },
    { name: "Name Change Passport Sahuarita AZ", description: "Passport name change service for Sahuarita, AZ residents." },
  ],
  testimonials: [
    {
      name: "David R.",
      location: "Freeport-McMoRan Engineer",
      rating: 5,
      text: "I'm a mining engineer at the Sierrita operations and got called to our Cerro Verde site in Peru with 4 days notice. My passport had expired. Fast Passport Center had me a new passport in 48 hours. Tucson is only 25 miles away — they moved incredibly fast.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia H.",
      location: "Green Valley, AZ",
      rating: 5,
      text: "Retired and planning a 6-month stay at our condo in San Carlos, Mexico. Realized my passport only had 4 months of validity — Mexico requires 6 months. Fast Passport Center got my renewal processed in 5 business days. I'm now enjoying my winter in Mexico.",
      service: "Expedited Renewal",
    },
    {
      name: "Robert & Linda M.",
      location: "Rancho Sahuarita",
      rating: 5,
      text: "Sahuarita couple planning a Mediterranean cruise for our 35th anniversary. Both passports expiring within 2 months of travel. Fast Passport Center handled both renewals in 7 business days. The personal agent was fantastic — called us with updates every step.",
      service: "Dual Renewal",
    },
    {
      name: "Carlos V.",
      location: "Tubac, AZ",
      rating: 5,
      text: "Tubac artist with gallery shows in San Miguel de Allende. My passport was stolen from my studio. Fast Passport Center helped with the DS-64 and got me a replacement in 4 business days. Didn't miss my opening. Remarkable service from Southern Arizona.",
      service: "Lost/Stolen Replacement",
    },
  ],
  facilities: [
    {
      id: "sah-1",
      name: "Sahuarita Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "350 W Sahuarita Rd",
      city: "Sahuarita",
      state: "AZ",
      zip: "85629",
      phone: "(520) 648-2160",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Sahuarita",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Sahuarita and Santa Cruz Valley residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sah-2",
      name: "Green Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 E Continental Rd",
      city: "Green Valley",
      state: "AZ",
      zip: "85614",
      phone: "(520) 625-4243",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Green Valley — 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Green Valley and northern Sahuarita residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sah-3",
      name: "Nogales Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 N Morley Ave",
      city: "Nogales",
      state: "AZ",
      zip: "85621",
      phone: "(520) 287-2850",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles south in Nogales",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Santa Cruz Valley and border community residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sah-4",
      name: "Pima County Clerk — Green Valley Justice Court",
      type: "County Clerk Passport Acceptance",
      address: "601 N La Cañada Dr",
      city: "Green Valley",
      state: "AZ",
      zip: "85614",
      phone: "(520) 648-2197",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles south in Green Valley",
      website: "https://www.sc.pima.gov/justice-courts/green-valley",
      websiteLabel: "Green Valley Justice Court",
      notes: "Convenient county acceptance facility for Green Valley and Sahuarita residents.",
      icon: "ri-government-line",
    },
    {
      id: "sah-5",
      name: "Tucson Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1501 S Cherrybell Stra",
      city: "Tucson",
      state: "AZ",
      zip: "85726",
      phone: "(520) 629-9200",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "25 miles north in Tucson",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest acceptance facility in the region, convenient for Sahuarita commuters working in Tucson.",
      icon: "ri-mail-line",
    },
    {
      id: "sah-6",
      name: "Sahuarita Town Hall",
      type: "Municipal Passport Acceptance",
      address: "375 W Sahuarita Center Way",
      city: "Sahuarita",
      state: "AZ",
      zip: "85629",
      phone: "(520) 822-8800",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Sahuarita Town Center",
      website: "https://sahuaritaaz.gov",
      websiteLabel: "Town of Sahuarita",
      notes: "Town Hall offers limited passport acceptance hours. Call ahead to confirm availability before visiting.",
      icon: "ri-building-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "25 miles — 30-min drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking 'It's Only 25 Miles, I'll Just Go to Tucson'",
      description: "Yes, Sahuarita is only 25 miles from the Western Passport Center in Tucson — but that doesn't mean you can walk in. The agency requires pre-scheduled NPIC appointments, and slots fill weeks in advance. Don't waste even a short drive without confirming your appointment.",
    },
    {
      icon: "ri-image-line",
      title: "Green Valley Drugstore Photos for Mining Engineers",
      description: "Mining engineers heading to South America need passport photos that meet both U.S. and destination country standards. CVS and Walgreens in Green Valley may not have technicians trained for international visa photos. Verify all photo requirements before getting pictures taken.",
    },
    {
      icon: "ri-file-list-line",
      title: "Snowbirds Confusing Renewal Eligibility",
      description: "Green Valley snowbirds who spend half the year in Sahuarita and half abroad often renew while outside the U.S. Passports renewed abroad have different processing rules. If you have a passport issued by a U.S. embassy, confirm eligibility for mail-in DS-82 renewal.",
    },
    {
      icon: "ri-global-line",
      title: "Passport Card vs. Book for Mexico Border Crossings",
      description: "Sahuarita residents frequently cross into Nogales, Sonora for shopping and dining. A passport card works for land border crossings — but if you fly to Mexico City or Guadalajara, you need a passport book. Many Sahuarita travelers confuse the two.",
    },
    {
      icon: "ri-shield-cross-line",
      title: "Mining Engineers Forgetting Visa Requirements",
      description: "Freeport-McMoRan engineers traveling to South American mining operations often focus only on their passport and forget work visa requirements. Chile, Peru, and Argentina all require business visas for mining work. Your passport must be valid at least 6 months beyond your visa expiration.",
    },
    {
      icon: "ri-restart-line",
      title: "Green Valley Retirees Not Checking 6-Month Validity Rule",
      description: "Green Valley snowbirds heading to Mexico for the winter are often surprised that Mexico — like many countries — requires 6 months of passport validity beyond your stay. Check your expiration date 8 months before planned travel to avoid last-minute scrambles.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Sahuarita, Green Valley, or Tucson." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — only 25 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sahuarita or Santa Cruz Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Sahuarita from the Tucson passport agency?",
      a: "Sahuarita is only 25 miles from the Western Passport Center in Tucson — about a 30-minute drive via I-19. This makes Sahuarita one of the closest Arizona communities to a passport agency. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins.",
    },
    {
      q: "I work at the Freeport-McMoRan Sierrita mine — how fast can I get a passport for South American travel?",
      a: "Sierrita mining engineers and contractors traveling to Peru, Chile, or Argentina are among our most frequent Sahuarita clients. Emergency processing delivers passports in 24–48 hours. Standard expedited processing runs 2–9 business days. We recommend also checking work visa requirements for your destination country.",
    },
    {
      q: "Does Sahuarita have its own passport acceptance facility?",
      a: "Yes. The Sahuarita Post Office at 350 W Sahuarita Road is a USPS passport acceptance facility. The Sahuarita Town Hall also offers limited passport acceptance hours. For fastest processing, pair your in-person DS-11 application with Fast Passport Center's courier service to Tucson.",
    },
    {
      q: "How fast can I get an emergency passport in Sahuarita?",
      a: "Emergency processing is typically 24–48 hours for Sahuarita residents. Given Sahuarita's proximity — only 25 miles to Tucson — our courier turnaround is among the fastest in Arizona. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Can Green Valley retirees use your service too?",
      a: "Absolutely. Green Valley retirees and snowbirds are a significant portion of our Southern Arizona clients. We serve all of the Santa Cruz Valley including Green Valley, Tubac, Amado, Rio Rico, and Nogales. Our courier service handles everything — no drive to Tucson required.",
    },
    {
      q: "Where do Sahuarita travelers fly internationally?",
      a: "Sahuarita travelers typically fly internationally through Tucson International Airport (TUS, about 25 miles north) for domestic connections, or Phoenix Sky Harbor International Airport (PHX, about 125 miles north) for direct international flights. Both require a valid passport book.",
    },
    {
      q: "I'm a snowbird — can I renew my passport while in Sahuarita for the winter?",
      a: "Yes. Many Green Valley and Sahuarita snowbirds renew their U.S. passports during their Arizona winter stay. Fast Passport Center processes renewals in 2–9 business days, so you'll have your new passport before heading back north or abroad. Just make sure you use a Sahuarita mailing address for delivery.",
    },
    {
      q: "How do I renew my passport in Sahuarita?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Sahuarita?",
      a: "Walgreens at 18705 S I-19 Frontage Road in Green Valley is the closest passport photo location. There's also a CVS Pharmacy in Green Valley. UPS Store locations in Green Valley offer photo services too. Always verify your photos meet exact 2x2 inch specifications.",
    },
    {
      q: "Can I use a passport card to cross into Nogales, Sonora?",
      a: "Yes — a passport card is valid for land border crossings into Mexico at the Nogales port of entry. However, if you plan to fly anywhere in Mexico or travel beyond the border zone, you need a full passport book. Sahuarita travelers should carry a passport book for maximum flexibility.",
    },
    {
      q: "I need a work visa for Chile — does my passport need extra validity?",
      a: "Yes. For most South American work visas — including Chile, Peru, and Argentina — your U.S. passport must be valid at least 6 months beyond your visa's expiration date. Sahuarita mining engineers should check both passport validity and visa requirements 3–4 months before planned travel.",
    },
    {
      q: "Does the Sahuarita post office take passport appointments on Saturdays?",
      a: "No. The Sahuarita Post Office only processes passport applications Monday through Friday from 9:00 AM to 4:00 PM. Saturday appointments are not available. Plan your visit during weekday hours and call ahead to confirm passport service availability.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Green Valley", address: "18705 S I-19 Frontage Rd, Green Valley, AZ 85614", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Green Valley", address: "190 W Continental Rd, Green Valley, AZ 85614", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Green Valley", address: "270 W Continental Rd, Ste 104, Green Valley, AZ 85614", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "Walgreens — Tucson (South)", address: "3835 S 6th Ave, Tucson, AZ 85714", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Tucson (Midtown)", address: "7845 E Broadway Blvd, Tucson, AZ 85710", hours: "Mon–Sun: 8AM–9PM" },
    { name: "FedEx Office — Tucson", address: "4700 N Oracle Rd, Tucson, AZ 85705", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Green Valley", address: "601 N La Cañada Dr, Green Valley, AZ 85614", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Green Valley and Sahuarita residents. Closest MVD to Sahuarita.", featured: true },
    { name: "AZ MVD — Tucson South", address: "3565 S Broadmont Dr, Tucson, AZ 85713", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves southern Tucson metro including Sahuarita and Santa Cruz Valley.", featured: false },
    { name: "AZ MVD — Nogales", address: "3036 N Grand Ave, Nogales, AZ 85621", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves southern Santa Cruz County and border communities.", featured: false },
  ],
  airports: [
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "25 miles north of Sahuarita",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Closest airport to Sahuarita with domestic and limited international routes. Only 30 minutes north via I-19. Co-located with the Western Passport Center — incredibly convenient for urgent passport needs.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "125 miles north of Sahuarita",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 2 hours north via I-19 and I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "NGL",
      name: "Nogales International Airport",
      distance: "25 miles south of Sahuarita",
      address: "1500 N Grand Ave, Nogales, AZ 85621",
      note: "Small general aviation airport near the border. Limited to private and charter flights. International travelers use TUS or PHX for commercial international departures.",
      flightUrl: "https://www.nogalesaz.gov/",
      flightLabel: "Nogales Airport",
      featured: false,
    },
  ],
};